import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { PageHeader, Empty } from "../../components/common";

const inp = "w-full bg-muted/60 border h-9 px-2 text-sm";

const Templates = ({ initial }) => {
  const [tpls, setTpls] = useState(initial);
  const set = (i, k, v) => setTpls(tpls.map((t, j) => (j === i ? { ...t, [k]: v } : t)));
  const save = () => api.put("/admin/whatsapp-templates", { templates: tpls }).then((r) => { setTpls(r.data.templates); toast.success("Messages saved"); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="ax-panel p-4" data-testid="wa-templates">
      <div className="ax-label mb-1">Message wording</div>
      <p className="text-xs text-muted-foreground mb-3">Agents can edit before sending. The first message is used by the one-tap "Didn't pick up" button. Placeholders: {"{name}"} {"{company}"} {"{agent}"} {"{requirement}"}</p>
      {tpls.map((t, i) => (
        <div key={t.template_id || i} className="border p-3 mb-2 space-y-2">
          <div className="flex gap-2"><input data-testid={`wa-tpl-name-${i}`} className={inp} value={t.name} onChange={(e) => set(i, "name", e.target.value)} placeholder="Name e.g. Missed call" />
            <button data-testid={`wa-tpl-remove-${i}`} onClick={() => setTpls(tpls.filter((_, j) => j !== i))} className="border px-2"><Trash2 className="h-4 w-4" /></button></div>
          <textarea data-testid={`wa-tpl-text-${i}`} className="w-full h-24 bg-muted/60 border p-2 text-sm" value={t.text} onChange={(e) => set(i, "text", e.target.value)} />
        </div>))}
      <div className="flex gap-2">
        <button data-testid="wa-tpl-add" onClick={() => setTpls([...tpls, { name: "", text: "" }])} className="border h-9 px-3 text-sm flex items-center gap-1"><Plus className="h-4 w-4" />Add message</button>
        <button data-testid="wa-tpl-save" onClick={save} className="bg-primary text-primary-foreground h-9 px-5 text-sm">Save messages</button>
      </div>
    </div>
  );
};

const Library = ({ files, reload }) => {
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [inputKey, setInputKey] = useState(0);
  const upload = () => {
    const fd = new FormData(); fd.append("file", file); fd.append("name", name);
    setBusy(true);
    api.post("/admin/library", fd).then(() => { toast.success("File added"); setFile(null); setName(""); setInputKey(inputKey + 1); reload(); }).catch((e) => toast.error(errMsg(e))).finally(() => setBusy(false));
  };
  const remove = (f) => api.post(`/admin/library/${f.file_id}/remove`).then(() => { toast.success("File removed"); reload(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="ax-panel p-4" data-testid="wa-library">
      <div className="ax-label mb-1">Attachment library</div>
      <p className="text-xs text-muted-foreground mb-3">Company profile, brochures, catalogues. PDF, images, PPT or Word, up to 20 MB. Removing a file also stops its old links.</p>
      <div className="grid sm:grid-cols-3 gap-2 mb-3">
        <input data-testid="wa-file-name" className={inp} placeholder="Display name e.g. Company Profile 2026" value={name} onChange={(e) => setName(e.target.value)} />
        <input key={inputKey} data-testid="wa-file-input" type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.ppt,.pptx,.doc,.docx" onChange={(e) => setFile(e.target.files[0] || null)} className="text-xs self-center" />
        <button data-testid="wa-file-upload" disabled={!file || busy} onClick={upload} className="bg-primary text-primary-foreground h-9 text-sm disabled:opacity-50">{busy ? "Uploading…" : "Upload"}</button>
      </div>
      {!files.length ? <Empty text="No files yet." /> : files.map((f) => (
        <div key={f.file_id} className="flex items-center gap-2 border-t py-2 text-sm" data-testid={`wa-file-row-${f.file_id}`}>
          <span className="flex-1">{f.name}</span><span className="text-xs text-muted-foreground num">{(f.size / 1048576).toFixed(1)} MB</span>
          <button data-testid={`wa-file-remove-${f.file_id}`} onClick={() => remove(f)} className="border px-2 py-1 text-xs">Remove</button>
        </div>))}
    </div>
  );
};

export default function AdminWhatsApp() {
  const [cfg, setCfg] = useState(null);
  const load = useCallback(() => api.get("/whatsapp/config").then((r) => setCfg(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  if (!cfg) return null;
  return (
    <div>
      <PageHeader eyebrow="Lead follow-up" title="WhatsApp & Files" desc="When a lead doesn't pick up, the agent taps WhatsApp on the lead. WhatsApp opens on their phone or computer with this message and the chosen file links ready to send." />
      <div className="grid lg:grid-cols-2 gap-4"><Templates initial={cfg.templates} /><Library files={cfg.files} reload={load} /></div>
    </div>
  );
}
